import ObjectID from 'bson-objectid';
import { defineStore } from 'pinia';
import type { Question } from 'shared-domains';
import Constants from '~/config/Constants';
import QuestionFactory from '~/editor/QuestionFactory';
import NewSlideService from '~/services/NewSlideService';
import QuizizzAIService from '~/services/apis/QuizizzAIService';

enum AIQuizGenModes {
  USING_OPEN_TEXT = 'USING_OPEN_TEXT',
  USING_FILE_UPLOAD = 'USING_FILE_UPLOAD',
}

export interface GeneratedQuestion {
  questionId?: string,
  questionText: string,
  optionsText: string[],
  answer: number,
}

export interface IQuestionSuggestionPayload {
  lastQuestion?: GeneratedQuestion,
  currentIndex?: number,
  title?: string,
}

export interface IAIGenerated {
  isAIGenerated: boolean,
  isAIGeneratedFromExtraction: boolean,
  documentScannedByAIPercent: number,
  aiGenerationType: AIQuizGenModes | null,
}

export interface IQfwGptStore {
  aiGenerated: IAIGenerated,
  prevQuestionCreated: GeneratedQuestion | null,
  nextSuggestedQuestion: { question: Question | null; index: number; uniqueId: string; title: string },
  isNextQuestionSuggestionLoading: boolean,
  showSuggestedQuestionContainer: boolean,
  errorWhileQuestionGeneration: boolean,
}

const useQfwGPTStore = defineStore('qfwGPT', {
  state: (): IQfwGptStore => ({
    aiGenerated: {
      isAIGenerated: false,
      isAIGeneratedFromExtraction: false,
      documentScannedByAIPercent: 0,
      aiGenerationType: null,
    },
    prevQuestionCreated: null,
    nextSuggestedQuestion: { question: null, index: -1, uniqueId: '', title: '' },
    isNextQuestionSuggestionLoading: false,
    showSuggestedQuestionContainer: false,
    errorWhileQuestionGeneration: false,
  }),

  getters: {
    isAIGenerated(state) {
      return state.aiGenerated.isAIGenerated;
    },

    isAIGeneratedFromExtraction(state) {
      return state.aiGenerated.isAIGeneratedFromExtraction;
    },

    documentScannedByAIPercent(state) {
      return state.aiGenerated.documentScannedByAIPercent;
    },

    aiGenerationType(state) {
      return state.aiGenerated.aiGenerationType;
    },
  },
  actions: {
    async getNextQuestionSuggestion(options: { question?: Question; currentIndex: number; regenerate?: boolean; quizId: string }) {
      const {
        question, currentIndex, regenerate = false, quizId,
      } = options;
      this.errorWhileQuestionGeneration = false;
      if (currentIndex !== this.nextSuggestedQuestion.index || !this.nextSuggestedQuestion.uniqueId) {
        this.nextSuggestedQuestion.uniqueId = ObjectID().toHexString();
      }

      if (this.isNextQuestionSuggestionLoading) {
        return;
      }

      let questionText;
      let optionsText;
      let answer;
      let questionId;

      if (question) {
        questionText = question?.structure.query.text;
        optionsText = question?.structure.options.map(option => option?.text);
        answer = question?.structure.answer;
        questionId = question?._id;

        if (!questionText || question?.type !== Constants.QuestionTypes.MCQ) {
          this.showSuggestedQuestionContainer = false;
          return;
        }

        this.prevQuestionCreated = {
          questionText, optionsText, answer, questionId,
        };
      } else if (regenerate) {
        questionText = this.prevQuestionCreated?.questionText;
        optionsText = this.prevQuestionCreated?.optionsText;
        answer = this.prevQuestionCreated?.answer;
      }

      let payload: IQuestionSuggestionPayload = {};
      // If question is available make payload
      if (questionText && optionsText) {
        payload = {
          lastQuestion: {
            questionText,
            optionsText,
            answer,
          },
        };
      }
      payload.currentIndex = currentIndex;
      payload.title = this.nextSuggestedQuestion.title;
      if (!regenerate) {
        this.isNextQuestionSuggestionLoading = true;
      }

      const response = await QuizizzAIService.getNextQuestionSuggestion(quizId, payload);
      const suggestedQuestion: GeneratedQuestion = response?.data.questionData;

      if (!response?.success) {
        this.showSuggestedQuestionContainer = false;
        this.isNextQuestionSuggestionLoading = false;
        this.errorWhileQuestionGeneration = true;
        return;
      }

      let newQuestion = QuestionFactory.createQuestion(Constants.QuestionTypes.MCQ);
      newQuestion = NewSlideService.populateQuestionOptionText(newQuestion, suggestedQuestion);

      this.nextSuggestedQuestion = {
        question: newQuestion,
        index: currentIndex,
        uniqueId: this.nextSuggestedQuestion.uniqueId,
        title: response?.data.quizTitle,
      };
      if (!regenerate) {
        this.isNextQuestionSuggestionLoading = false;
      }
    },

    resetSuggestedQuestion(shouldResetTitle: boolean = true) {
      this.errorWhileQuestionGeneration = false;
      this.prevQuestionCreated = null;
      this.nextSuggestedQuestion = shouldResetTitle ? { question: null, index: -1, uniqueId: '', title: '' } : { question: null, index: -1, uniqueId: '', title: this.nextSuggestedQuestion.title };
      this.isNextQuestionSuggestionLoading = false;
      this.showSuggestedQuestionContainer = false;
    },

    setAIGenerated(values: IAIGenerated) {
      this.aiGenerated.isAIGenerated = true;
      this.aiGenerated.isAIGeneratedFromExtraction = values.isAIGeneratedFromExtraction;
      this.aiGenerated.documentScannedByAIPercent = Number.parseInt(String(values.documentScannedByAIPercent), 10);
      this.aiGenerated.aiGenerationType = values.aiGenerationType;
    },

    resetAIGenerated() {
      this.aiGenerated.isAIGenerated = false;
      this.aiGenerated.isAIGeneratedFromExtraction = false;
      this.aiGenerated.documentScannedByAIPercent = 0;
      this.aiGenerated.aiGenerationType = null;
    },
  },
});

export default useQfwGPTStore;
